/**
 * The controlled document library.
 *
 * Shapes follow `library.py` (`list_documents`, `get_document`, `list_types`)
 * and migration 0008. Two things the router settled that matter here:
 * `site_id` means "applies at this site" (tenant-wide manuals carry no site
 * and are included), and visibility is applied in SQL, so what comes back
 * is what this principal may know exists.
 */

export const DOCUMENT_STATUSES = ["draft", "in_review", "issued", "withdrawn"] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const DOCUMENT_STATUS_LABEL: Record<DocumentStatus, string> = {
  draft: "Draft",
  in_review: "In review",
  issued: "Issued",
  withdrawn: "Withdrawn",
};

export type Classification = "public" | "internal" | "restricted";

export const REVISION_STATUSES = [
  "draft",
  "in_review",
  "approved",
  "issued",
  "superseded",
  "withdrawn",
  "rejected",
] as const;
export type RevisionStatus = (typeof REVISION_STATUSES)[number];

export interface DocumentRow {
  id: string;
  title: string;
  reference?: string;
  /** The pack's document type key: manual, procedure, certificate, drawing, report … */
  type: string;
  status: DocumentStatus;
  classification: Classification;
  /** null = applies at every site. */
  siteId?: string;
  tags: string[];
  createdAt: string;
  /** The current revision, when one is in force. */
  revision?: string;
  issuedOn?: string;
  expiresOn?: string;
  reviewDueOn?: string;
  revisionStatus?: RevisionStatus;
  revisions: number;
  /* What a register row needs without a second call (library.py `list_documents`, build 2). */
  /** The type's category as the pack files it: certificate, controlled, record … */
  category?: string;
  /** The document's own attrs — for a certificate, its asset, kind and accountable post. */
  attrs?: Record<string, unknown>;
  /** The revision in force, by id, and its attrs (for a certificate, who issued it). */
  revisionId?: string;
  revisionAttrs?: Record<string, unknown>;
  /** Whether the revision in force has its file. */
  hasFile?: boolean;
  /** The newest revision, which is not the one in force when a renewal is waiting to be issued. */
  latest?: LatestRevision;
}

export interface LatestRevision {
  id: string;
  revision: string;
  status: RevisionStatus;
  issuedOn?: string;
  expiresOn?: string;
  at: string;
  hasFile: boolean;
}

export interface Revision {
  id: string;
  revision: string;
  sequence: number;
  status: RevisionStatus;
  issuedOn?: string;
  effectiveFrom?: string;
  supersededAt?: string;
  expiresOn?: string;
  reviewDueOn?: string;
  changeNote?: string;
  rejectedReason?: string;
  createdAt: string;
  author?: string;
  approvedBy?: string;
  approvedAt?: string;
  sha256?: string;
  bytes?: number;
  contentType?: string;
  /** The revision's own attrs — for a certificate's issue, who issued it. */
  attrs?: Record<string, unknown>;
}

export interface DocumentLink {
  entityKind: string;
  entityId: string;
  relation?: string;
}

export interface Acknowledgement {
  revision: string;
  revisionStatus: RevisionStatus;
  person: string;
  required: boolean;
  acknowledgedAt?: string;
  isCurrent: boolean;
}

export interface DocumentDetail extends DocumentRow {
  ownerId?: string;
  withdrawnAt?: string;
  withdrawnReason?: string;
  retentionUntil?: string;
  attrs?: Record<string, unknown>;
  revisionHistory: Revision[];
  links: DocumentLink[];
  acknowledgements: Acknowledgement[];
}

export interface DocumentType {
  key: string;
  name: string;
  description?: string;
  category?: string;
  controlled: boolean;
  requiresApproval: boolean;
  reviewMonths?: number;
  expires: boolean;
  retentionYears?: number;
  defaultClassification?: Classification;
  pack: string;
}

export type Attention = "expired" | "expiring" | "review_overdue" | "review_due" | "ok";

/** What a document controller needs to look at, with 30-day horizons. */
export function attention(d: DocumentRow, now: Date): Attention {
  if (d.status !== "issued") return "ok";
  const horizon = new Date(now.getTime() + 30 * 86_400_000);
  if (d.expiresOn) {
    const e = new Date(d.expiresOn);
    if (e < now) return "expired";
    if (e < horizon) return "expiring";
  }
  if (d.reviewDueOn) {
    const r = new Date(d.reviewDueOn);
    if (r < now) return "review_overdue";
    if (r < horizon) return "review_due";
  }
  return "ok";
}

export const ATTENTION_LABEL: Record<Attention, string> = {
  expired: "Expired",
  expiring: "Expires within 30 days",
  review_overdue: "Review overdue",
  review_due: "Review due within 30 days",
  ok: "",
};

export function sortDocuments(rows: readonly DocumentRow[], now: Date): DocumentRow[] {
  const rank: Record<Attention, number> = { expired: 0, review_overdue: 1, expiring: 2, review_due: 3, ok: 4 };
  return [...rows].sort((a, b) => rank[attention(a, now)] - rank[attention(b, now)] || a.title.localeCompare(b.title));
}

/* ---- writing to the library ---------------------------------------------------- */

export const REVISION_STATUS_LABEL: Record<RevisionStatus, string> = {
  draft: "Draft",
  in_review: "In review",
  approved: "Approved",
  issued: "Issued",
  superseded: "Superseded",
  withdrawn: "Withdrawn",
  rejected: "Rejected",
};

/**
 * The revision lifecycle, mirrored from `library/documents.py`
 * REVISION_TRANSITIONS so the screen offers only moves the platform will
 * consider. The platform still decides: draft → issued is refused for a type
 * that requires approval, and the author can never approve their own work.
 */
export const REVISION_TRANSITIONS: Record<RevisionStatus, readonly RevisionStatus[]> = {
  draft: ["in_review", "issued", "withdrawn"],
  in_review: ["approved", "rejected", "draft"],
  approved: ["issued", "withdrawn"],
  issued: ["superseded", "withdrawn"],
  rejected: ["draft", "withdrawn"],
  superseded: [],
  withdrawn: [],
};

/**
 * What the screen offers from a revision's status. `superseded` is left out:
 * the platform sets it itself when the next revision is issued, and a person
 * choosing it by hand would leave the document with nothing in force.
 * draft → issued is offered only when the type does not require approval.
 */
export function offeredTransitions(status: RevisionStatus, type: DocumentType | undefined): RevisionStatus[] {
  return REVISION_TRANSITIONS[status].filter((to) => {
    if (to === "superseded") return false;
    if (status === "draft" && to === "issued" && (type?.requiresApproval ?? true)) return false;
    return true;
  });
}

/** Approving and issuing are certification acts (`document:approve`); the rest is authorship. */
export function transitionPermission(to: RevisionStatus): "document:approve" | "document:write" {
  return to === "approved" || to === "issued" ? "document:approve" : "document:write";
}

export const TRANSITION_VERB: Record<RevisionStatus, string> = {
  draft: "Return to draft",
  in_review: "Submit for review",
  approved: "Approve",
  issued: "Issue",
  rejected: "Reject",
  withdrawn: "Withdraw revision",
  superseded: "Supersede",
};

export interface NewDocumentInput {
  type: string;
  title: string;
  /** Omit for a tenant-wide document (applies at every site). */
  siteId?: string;
  reference?: string;
  classification?: Classification;
  tags: string[];
}

export interface NewDocumentResult {
  id: string;
  title: string;
  reference?: string;
  classification: Classification;
  status: DocumentStatus;
  controlled: boolean;
}

export interface NewRevisionInput {
  revision: string;
  changeNote?: string;
  /** ISO date. */
  issuedOn?: string;
  /** ISO date; the platform requires it for a type that declares an expiry. */
  expiresOn?: string;
  file?: File;
}

export interface NewRevisionResult {
  id: string;
  revision: string;
  sequence: number;
  status: RevisionStatus;
  reviewDueOn?: string;
  expiresOn?: string;
}

export interface RevisionTransitionInput {
  to: RevisionStatus;
  /** Required by the screen for a rejection; stored as the rejected reason. */
  note?: string;
  /** Importing an archive: when the revision actually came into force. */
  effectiveFrom?: string;
}

export interface RevisionTransitionResult {
  revisionId: string;
  from: RevisionStatus;
  to: RevisionStatus;
}

/**
 * The acknowledgement this person still owes on the document, if any. The
 * platform keys acknowledgements by principal and only ever marks the
 * caller's own row, so matching on display name here can at most show a
 * button that does nothing — never acknowledge on someone else's behalf.
 */
export function pendingAcknowledgement(doc: DocumentDetail, personName: string): Acknowledgement | undefined {
  return doc.acknowledgements.find((a) => a.person === personName && a.required && !a.acknowledgedAt);
}

/** The revision row an acknowledgement refers to (by label — the platform's rows carry no revision id). */
export function revisionByLabel(doc: DocumentDetail, revision: string): Revision | undefined {
  return doc.revisionHistory.find((r) => r.revision === revision);
}
